import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Autocomplete,
  CircularProgress,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { EditOutlined, Visibility, VisibilityOff } from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { adminService } from "../services/adminService";
import {
  primaryDark,
  accentColor,
  buttonGradient,
  buttonGradientHover,
} from "../../../constants/consts";

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1); }
`;

const USER_TYPES = [
  { value: "admin", label: "Admin" },
  { value: "destek", label: "Destek" },
  { value: "user", label: "Kullanıcı" },
  { value: "rootUser", label: "Admin Kullanıcı" },
];

const TYPE_NEEDS_SUNUCU = (t) => t === "user" || t === "rootUser";

const normalize = (s = "") => String(s).toLocaleLowerCase("tr-TR").trim();

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    transition: "box-shadow 0.25s ease, background-color 0.25s ease",
    "&:hover": { backgroundColor: "rgba(4,87,129,0.02)" },
    "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(4,87,129,0.10)" },
  },
};

export default function UserEditDialog({
  open,
  user,
  sunucuKoduList = [],
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({
    id: "",
    username: "",
    phone: "",
    pass: "",
    email: "",
    sunucuKodu: "",
    type: "destek",
  });
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && user) {
      setForm({
        id: user.id ?? "",
        username: user.username ?? "",
        phone: user.phone ?? "",
        pass: "", // güvenlik — boş başlat
        email: user.email ?? "",
        sunucuKodu: user.sunucuKodu ?? "",
        type: user.type ?? "destek",
      });
      setError("");
      setSaving(false);
    }
  }, [open, user]);

  const needsSunucu = TYPE_NEEDS_SUNUCU(form.type);
  const typeOptions = USER_TYPES.map((t) => t.value);
  const typeLabel = (v) => USER_TYPES.find((t) => t.value === v)?.label || v;

  const handleChange = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.username.trim() || !form.email.trim()) {
      setError("Kullanıcı adı ve email zorunludur.");
      return;
    }
    if (needsSunucu && !form.sunucuKodu) {
      setError("Bu kullanıcı tipi için sunucu kodu zorunludur.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await adminService.editUser({
        id: form.id,
        username: form.username.trim(),
        phone: form.phone.trim(),
        // Şifre boşsa gönderilmez — backend eski hash'i korur
        pass: form.pass.trim() || null,
        email: form.email.trim(),
        sunucuKodu: form.sunucuKodu,
        type: form.type,
      });
      onSaved?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      setError("Kullanıcı güncellenemedi.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          animation: `${popIn} 0.25s cubic-bezier(0.22, 1, 0.36, 1) both`,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 1.75,
          background: `linear-gradient(135deg, ${primaryDark} 0%, ${accentColor} 100%)`,
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            color: "#fff",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <EditOutlined sx={{ fontSize: 20 }} />
          Kullanıcıyı Düzenle
        </DialogTitle>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <TextField
            label="Kullanıcı Adı *"
            value={form.username}
            onChange={handleChange("username")}
            fullWidth
            sx={inputSx}
          />
          <TextField
            label="Email *"
            value={form.email}
            onChange={handleChange("email")}
            fullWidth
            sx={inputSx}
          />
          <TextField
            label="Telefon"
            value={form.phone}
            onChange={handleChange("phone")}
            fullWidth
            sx={inputSx}
          />

          {/* Kullanıcı Tipi */}
          <Autocomplete
            size="small"
            options={typeOptions}
            value={form.type || null}
            onChange={(_, v) =>
              setForm((prev) => ({
                ...prev,
                type: v || "destek",
                sunucuKodu: TYPE_NEEDS_SUNUCU(v) ? prev.sunucuKodu : "",
              }))
            }
            getOptionLabel={typeLabel}
            disableClearable
            renderOption={(props, opt) => <li {...props}>{typeLabel(opt)}</li>}
            renderInput={(params) => (
              <TextField {...params} label="Kullanıcı Tipi *" sx={inputSx} />
            )}
          />

          {/* Sunucu Kodu — sadece user/rootUser için */}
          <Autocomplete
            size="small"
            options={sunucuKoduList}
            value={form.sunucuKodu || null}
            onChange={(_, v) =>
              setForm((prev) => ({ ...prev, sunucuKodu: v || "" }))
            }
            disabled={!needsSunucu}
            filterOptions={(opts, { inputValue }) =>
              opts.filter((o) => normalize(o).includes(normalize(inputValue)))
            }
            noOptionsText="Sunucu bulunamadı"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sunucu Kodu"
                placeholder={
                  needsSunucu ? "Yaz veya seç" : "Bu tip için gerekli değil"
                }
                helperText={
                  !needsSunucu ? "Sadece müşteri tipleri için geçerli" : ""
                }
                sx={inputSx}
              />
            )}
          />

          {/* Şifre */}
          <TextField
            label="Yeni Şifre"
            type={showPass ? "text" : "password"}
            value={form.pass}
            onChange={handleChange("pass")}
            placeholder="Boş bırakılırsa değişmez"
            fullWidth
            sx={{ ...inputSx, gridColumn: { xs: "1", sm: "1 / span 2" } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowPass((v) => !v)}
                  >
                    {showPass ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {error && (
          <Typography
            sx={{ mt: 2, color: "#B91C1C", fontSize: 12.5, fontWeight: 600 }}
          >
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          Vazgeç
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 700,
            background: buttonGradient,
            "&:hover": { background: buttonGradientHover },
            "&:disabled": { background: "#c7c7c7" },
          }}
        >
          {saving ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            "Kaydet"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
